/**
 * EIR view of assigned founders' data rooms. Lists founders with submission
 * progress; opening one loads that founder's full data room (getFounderDataRoom)
 * into the EIRDataRoomPopUp panel.
 *
 * Auto-opens a founder via location.state.openFounderId when navigated from the
 * EIR dashboard. EIR-only: other roles see a placeholder.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyFounders, getFounderDataRoom } from '../services/apiService';
import EIRDataRoomPopUp from '../components/EIRDataRoomPopUp';

/** EIR overview of assigned founders' data-room progress. */
export default function EIRDataRoom() {
  const { user } = useAuth();
  const location = useLocation();

  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Slide panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelFounder, setPanelFounder] = useState(null);
  const [dataRoomDetail, setDataRoomDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    getMyFounders()
      .then(setFounders)
      .finally(() => setLoading(false));
  }, []);

  // Auto-open a founder when navigated here from the dashboard
  useEffect(() => {
    if (loading || !founders.length) return;
    const id = location.state?.openFounderId;
    if (!id) return;
    const founder = founders.find((f) => f.id === id);
    if (founder) openFounder(founder);
  }, [loading, founders]); // openFounder is stable (useCallback)

  const openFounder = useCallback((founder) => {
    setPanelFounder(founder);
    setPanelOpen(true);
    setDataRoomDetail(null);
    setDetailLoading(true);
    getFounderDataRoom(founder.id)
      .then(setDataRoomDetail)
      .finally(() => setDetailLoading(false));
  }, []);

  const avgProgress = founders.length
    ? Math.round(founders.reduce((s, f) => s + (f.data_room?.progress || 0), 0) / founders.length)
    : 0;
  const totalUploaded = founders.reduce((s, f) => s + (f.data_room?.uploaded_count || 0), 0);

  if (user?.role !== 'eir') {
    return (
      <div style={styles.page}>
        <div style={styles.inner}>
          <p style={styles.dim}>This view is for EIRs. Use the Admin dashboard to review founder data rooms.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.header}>
          <h1 style={styles.title}>Data Rooms</h1>
          <p style={styles.subtitle}>
            Track document submission progress across your assigned founders.
          </p>
        </div>

        {/* Summary stats */}
        {!loading && founders.length > 0 && (
          <div style={styles.statsRow}>
            <StatCard label="Founders" value={founders.length} />
            <StatCard label="Avg. Progress" value={`${avgProgress}%`} />
            <StatCard label="Docs Uploaded" value={totalUploaded} />
          </div>
        )}

        {/* Founder list */}
        {loading && <p style={styles.dim}>Loading…</p>}
        {!loading && founders.length === 0 && (
          <p style={styles.dim}>No founders assigned yet.</p>
        )}
        {!loading && founders.length > 0 && (
          <div style={styles.table}>
            <div style={styles.tableHead}>
              <span style={styles.colHead}>Founder</span>
              <span style={styles.colHead}>Progress</span>
              <span style={styles.colHead}>Complete</span>
              <span style={styles.colHead}>Stage</span>
            </div>
            {founders.map((f) => {
              // No hardcoded item total: the denominator is the backend's count
              // of required items, and a stale copy here would contradict the
              // percentage sitting next to it.
              const dr = f.data_room || { progress: 0, completed_count: 0, total_count: 0 };
              return (
                <button key={f.id} style={styles.tableRow} onClick={() => openFounder(f)}>
                  <div style={styles.colName}>
                    <p style={styles.rowName}>{f.first_name} {f.last_name}</p>
                    {f.company_name && <p style={styles.rowCompany}>{f.company_name}</p>}
                  </div>
                  <div style={styles.colProgress}>
                    <div style={styles.progressTrack}>
                      <div style={{ ...styles.progressFill, width: `${dr.progress}%`, background: progressColor(dr.progress) }} />
                    </div>
                    <span style={styles.progressPct}>{dr.progress}%</span>
                  </div>
                  <span style={styles.colDocs}>{(dr.completed_count ?? dr.uploaded_count)}/{dr.total_count}</span>
                  <span style={styles.colStage}>
                    {f.company_stage ? (
                      <span style={{ ...styles.stagePill, ...stageColor(f.company_stage) }}>
                        {f.company_stage}
                      </span>
                    ) : '—'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

      </div>

      <EIRDataRoomPopUp
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        founder={panelFounder}
        data={dataRoomDetail}
        loading={detailLoading}
      />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={styles.statCard}>
      <p style={styles.statValue}>{value}</p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

// Progress-bar color by completion: green at 80%+, rust at 50%+, red below.
function progressColor(pct) {
  if (pct >= 80) return '#15803d';
  if (pct >= 50) return '#c66b47';
  return '#b91c1c';
}

function stageColor(stage) {
  const map = {
    idea:      { background: '#f0e8ff', color: '#5b21b6' },
    prototype: { background: '#fef9c3', color: '#854d0e' },
    pilot:     { background: '#e0f2fe', color: '#0369a1' },
    revenue:   { background: '#dcfce7', color: '#15803d' },
    scaling:   { background: '#fff7ed', color: '#c2410c' },
  };
  return map[stage] || { background: 'var(--color-mint-dark)', color: 'var(--color-forest)' };
}

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: "98%", margin: "0 auto" },
  header: { marginBottom: 'var(--space-6)', paddingTop: 'var(--space-4)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 64, fontWeight: 500, color: 'var(--color-rust)', lineHeight: 1.1, marginBottom: 'var(--space-2)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 22, fontWeight: 'var(--font-weight-page-subtitle)', color: 'var(--color-forest)', opacity: 0.6, lineHeight: 1.5 },
  dim: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.5, fontStyle: 'italic' },

  statsRow: { display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-6)', flexWrap: 'wrap', maxWidth: 800 },
  statCard: { background: 'var(--color-frost)', borderRadius: 'var(--radius-md)', padding: '16px 24px', boxShadow: 'var(--shadow-card)', flex: 1, textAlign: 'center' },
  statValue: { fontFamily: 'var(--font-accent)', fontSize: 28, fontWeight: 800, color: 'var(--color-forest)', lineHeight: 1.1, marginBottom: 4 },
  statLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em' },

  table: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', overflow: 'hidden' },
  tableHead: { display: 'grid', gridTemplateColumns: '2fr 2fr 80px 90px', padding: '10px 20px', background: 'var(--color-mint-dark)', gap: 16, alignItems: 'center' },
  colHead: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em' },
  tableRow: { display: 'grid', gridTemplateColumns: '2fr 2fr 80px 90px', padding: '14px 20px', gap: 16, alignItems: 'center', background: 'none', border: 'none', borderBottom: '1px solid var(--color-mint)', cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'background 0.12s' },

  colName:     { minWidth: 0 },
  colProgress: { display: 'flex', alignItems: 'center', gap: 10 },
  colDocs:     { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', textAlign: 'center' },
  colStage:    { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)' },

  rowName: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, color: 'var(--color-forest)' },
  rowCompany: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' },

  progressTrack: { flex: 1, height: 8, background: 'var(--color-mint)', borderRadius: 4, overflow: 'hidden', minWidth: 80 },
  progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.4s' },
  progressPct: { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', opacity: 0.7, minWidth: 32 },

  stagePill: { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 700, textTransform: 'capitalize' },
};

